import { QuestionType, type QuestionTypeValue } from '@/constants/QuestionType';

/**
 * The one place the survey's question-to-question movement is described. The hook that sequences
 * the move and the CSS that draws it both read these numbers, so putting the flow back to an
 * instant switch is a single edit here.
 *
 * `confirmMs` is shorter than the 800 ms glow the picked card plays: the manager has seen the
 * glow peak (half-way, at 400 ms) and start to fade before the panel takes the question away.
 */
export const SURVEY_QUESTION_MOTION = {
    /** How long the picked answer keeps the spotlight before the panel moves on. */
    confirmMs: 600,
    /** The outgoing question fading and sliding away. */
    leaveMs: 250,
    /** The incoming question fading and sliding in. */
    enterMs: 350,
    /** How far each half of the slide travels; its sign carries the direction. */
    travelPx: 60,
} as const;

/**
 * Question types whose answer is one pick on a discrete target: the click that chooses is the
 * whole answer, so the survey moves on by itself. Multi-select, ranking and free text keep the
 * explicit button — the manager is not done there when the first click lands.
 */
const SELF_ADVANCING_QUESTION_TYPES: ReadonlySet<QuestionTypeValue> = new Set([
    QuestionType.MULTI_CHOICE,
    QuestionType.YES_NO,
    QuestionType.EMOJI_CHOICE,
]);

/**
 * Question types that move on once the gesture ends rather than once a value arrives. A rating
 * scale reports a value the moment it is touched, before the manager has moved anything, so the
 * value alone does not mean one was chosen — the released finger does.
 */
const COMMIT_ADVANCING_QUESTION_TYPES: ReadonlySet<QuestionTypeValue> = new Set([
    QuestionType.RATING_SCALE,
]);

export const advancesOnAnswer = (questionType: QuestionTypeValue): boolean =>
    SELF_ADVANCING_QUESTION_TYPES.has(questionType);

export const advancesOnCommit = (questionType: QuestionTypeValue): boolean =>
    COMMIT_ADVANCING_QUESTION_TYPES.has(questionType);
